import React, { useEffect, useMemo, useRef, useState } from "react";
import { Table } from "../Table/Table";
import { Column } from "../../types/table";
import { getAuthors } from "../../services/firebase";
import { AuthorType } from "../../types/blog";
import { Pen, X } from "lucide-react";
import BlogAuthor from "../Blog/BlogAuthor";

const BlogAuthorsTab = () => {
  const [loading, setLoading] = useState(true);
  const [authors, setAuthors] = useState<AuthorType[]>([]);
  const [selectedAuthor, setSelectedAuthor] = useState<AuthorType | null>(null);
  const [isOpen, setIsOpen] = React.useState(false);
  const authorEditModalRef = useRef<HTMLDivElement>(null);
  const [editMode, setEditMode] = useState(false);

  const authorOptions = useMemo(() => {
    return authors.map((author) => ({
      id: author.id,
      name: author.name,
      image: author.image,
      thumbnail: author.thumbnail,
    }));
  }, [authors]);

  const fetchAuthors = async () => {
    try {
      const authorsList = await getAuthors();
      setAuthors(authorsList);
      setLoading(false);
    } catch (error) {
      console.error("Failed to fetch authors:", error);
    }
  };

  useEffect(() => {
    fetchAuthors();
  }, [authors]);

  const columns: Column<AuthorType>[] = useMemo(
    () => [
      {
        header: "Author Profile Picture",
        accessor: "image",
        render: (_, data: AuthorType) => (
          <img
            loading="lazy"
            src={
              data.thumbnail ||
              `https://ui-avatars.com/api/?name=${encodeURIComponent(
                data.name
              )}&background=random&size=32`
            }
            alt={data.name}
            className="w-6 h-6 rounded-full object-cover"
            onError={(e) =>
              (e.currentTarget.src = `https://ui-avatars.com/api/?name=${encodeURIComponent(
                data.name
              )}&background=random&size=32`)
            }
          />
        ),
      },
      { header: "Author Name", accessor: "name" },
      {
        header: "Action",
        accessor: "id",
        render: (_id, data: AuthorType) => (
          <button onClick={() => handleEdit(data)}>
            <Pen />
          </button>
        ),
      },
    ],
    []
  );
  const handleEdit = (authorData: AuthorType) => {
    setSelectedAuthor(authorData);
    setEditMode(true);
    setIsOpen(true);
  };

  const handleSaveSuccess = () => {
    setIsOpen(false);
    setEditMode(false);
  };

  if (loading) return <div>Loading...</div>;
  return (
    <>
      <div className="topBtnWrap">
        <button onClick={() => setIsOpen(true)} className="btnStyle">
          Add New Author
        </button>
      </div>
      <Table columns={columns} data={authorOptions} />

      {isOpen && (
        <div className="fixed inset-0 bg-black bg-opacity-50 z-50 flex items-center justify-center p-4">
          <div
            ref={authorEditModalRef}
            className="bg-white rounded-lg w-[90vw] max-w-[1200px] max-h-[90vh] overflow-y-auto relative top-8"
          >
            <div className="sticky top-0 bg-white p-4 border-b flex justify-between items-center">
              <div>
                <h2 className="text-lg font-bold">
                  {editMode ? "Edit" : "Add New"} Author
                </h2>
                <p className="text-gray-600">
                  {editMode ? "Edit" : "Add"} Author details
                </p>
              </div>
              <button
                onClick={() => {
                  setIsOpen(false);
                  setEditMode(false);
                }}
                className="p-2 hover:bg-gray-100 rounded-full transition-colors"
              >
                <X size={20} />
              </button>
            </div>

            <div className="p-4">
              <BlogAuthor
                onSaveSuccess={handleSaveSuccess}
                blogAuthorId={editMode ? selectedAuthor?.id : undefined}
              />
            </div>
          </div>
        </div>
      )}
    </>
  );
};

export default BlogAuthorsTab;
